import { CdkScrollable } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { PlaylistTrack, Track } from '@app/core/models/Track';
import { HotkeysService } from '@app/core/services/hotkeys.service';
import {
  SortPredicate,
  TracksTableDirective,
} from '@app/library/components/content/tracks-table/tracks-table.directive';
import { ColumnShown } from '@app/library/models/column-shown.model';
import { Playlist } from '@app/library/models/playlist.model';
import { LibraryModalService } from '@app/library/services/library-modal.service';
import { BacsState } from '@app/library/states/bacs.state';
import { StartEditMultipleTracks } from '@app/library/states/editing-track.actions';
import { TogglePlaylistColumn } from '@app/library/states/playlist-columns.actions';
import { DeletePlaylistTracksRequest } from '@app/library/states/playlist-tracks.actions';
import {
  AddToPlaylistSuccess,
  StartEditPlaylist,
} from '@app/library/states/playlists.actions';
import {
  SelectMultiplePlayTracks,
  SelectPlayTrack,
  UnselectAll,
  UnselectPlayTrack,
} from '@app/library/states/selected-tracks.actions';
import { SelectedTracksState } from '@app/library/states/selected-tracks.state';
import { TrackCuesRequest } from '@app/library/states/track-mix-points.actions';
import { TracksState } from '@app/library/states/tracks.state';
import { SavedToLocalstorage } from '@app/shared/constants';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import {
  OpenSpecificPanel,
  PANELS_KEYS,
  TryActionUnlessPanelIsBlocking,
} from '@radioking/shared/pannel-manager';
import { slideInOutBlock } from '@radioking/ui/animations';
import amplitude from 'amplitude-js';
import { BehaviorSubject, Observable } from 'rxjs';
import { delay, filter, map, take, tap, throttleTime } from 'rxjs/operators';

@Component({
  selector: 'rk-playlist-audio-table',
  templateUrl: './playlist-audio-table.component.html',
  styleUrls: ['./playlist-audio-table.component.scss'],
  animations: [slideInOutBlock],
})
export class PlaylistAudioTableComponent
  extends TracksTableDirective<PlaylistTrack>
  implements OnInit, AfterViewInit
{
  @Input()
  columns: ColumnShown;

  @Input()
  playlist: Playlist;

  @Input()
  processing = false;

  @Input()
  searchField$: Observable<string>;

  @Output()
  readonly resetSortEvent = new EventEmitter<void>();

  @ViewChild('defaultSort')
  defaultSortButton: ElementRef;

  @ViewChild(CdkScrollable) cdkScrollable: CdkScrollable;

  @Select(SelectedTracksState.numberPlayTrackSelected)
  numberTracksSelected$: Observable<number>;

  isDefaultSort = true;

  predicate: SortPredicate = 'position';
  reverse = true;

  isMobile = false;
  lastAddedPosition = new BehaviorSubject(-1);
  readonly preferredTableSortStorageKey =
    SavedToLocalstorage.PREFERENCE_TABLE_SORT_PLAYLIST;

  constructor(
    protected readonly store: Store,
    protected readonly modalService: LibraryModalService,
    protected readonly actions$: Actions,
    protected readonly dialog: MatDialog,
    protected readonly hotkeys: HotkeysService,
    protected readonly cdr: ChangeDetectorRef,
    private readonly platform: Platform,
  ) {
    super(store, modalService, actions$, dialog, hotkeys, cdr);
    this.subscription.add(
      this.actions$
        .pipe(ofActionDispatched(SelectPlayTrack))
        .subscribe(({ track }: SelectPlayTrack) => {
          this.lastSelectedTrackId = this.getTrackId(track);
        }),
    );
    this.subscription.add(
      this.actions$
        .pipe(ofActionDispatched(UnselectPlayTrack))
        .subscribe(({ track }: UnselectPlayTrack) => {
          if (this.lastSelectedTrackId === this.getTrackId(track)) {
            this.lastSelectedTrackId = -1;
          }
        }),
    );
    this.subscription.add(
      this.actions$.pipe(ofActionDispatched(UnselectAll)).subscribe(() => {
        this.lastSelectedTrackId = -1;
      }),
    );
    this.isMobile = this.platform.ANDROID || this.platform.IOS;

    this.subscription.add(
      this.actions$
        .pipe(ofActionDispatched(TogglePlaylistColumn), delay(100))
        .subscribe(() => {
          this.updateTagZoneSize();
        }),
    );

    this.subscription.add(
      this.tracksObservable$.subscribe(res => {
        // Compute the size to display tags
        // Useful if the playlist was empty
        if (res.length > 0 && this.tagDisplayedLimiter === 0) {
          setTimeout(() => {
            this.updateTagZoneSize();
          }, 100);
        }
      }),
    );
    this.subscription.add(
      this.actions$
        .pipe(
          ofActionDispatched(AddToPlaylistSuccess),
          filter(
            ({ playlistId }: AddToPlaylistSuccess) => playlistId === this.playlist.id,
          ),
          tap(() => {
            this.lastAddedPosition.next(-1);
          }),
          delay(200),
          tap(({ position }: AddToPlaylistSuccess) => {
            if (!position && this.cdkScrollable) {
              this.cdkScrollable.scrollTo({
                top: this.tracks.length * 60,
              });
            }
            this.lastAddedPosition.next(position ? position : this.tracks.length);
          }),
          delay(2000), // Reset lastAddedPosition after animation
          tap(() => {
            this.lastAddedPosition.next(-1);
          }),
        )
        .subscribe(),
    );
    this.selectedTracksObserver(this.numberTracksSelected$);
  }

  ngOnInit() {
    this.subscription.add(
      this.searchField$.subscribe(s => {
        this.predicate =
          s.length && this.predicate === 'position' ? 'none' : this.predicate;
      }),
    );
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.subscription.add(
      this.resizeObservable.pipe(throttleTime(250)).subscribe(() => {
        if (this.store.selectSnapshot(SelectedTracksState.numberPlayTrackSelected) > 0) {
          this.updateHeaderActions();
        }
      }),
    );
  }

  getSortedArray(prop: SortPredicate, revert = false): PlaylistTrack[] {
    this.isDefaultSort = prop === 'position' && !revert;

    return super.getSortedArray(prop, revert);
  }

  resetSort() {
    this.resetSortEvent.emit();
    if (this.predicate !== 'position' || !this.reverse) {
      this.defaultSortButton?.nativeElement.click();
    }
  }

  detectScroll() {
    if (this.table && !this.platform.FIREFOX) {
      const totalTableHeight = this.getTableHeight() + 248;
      this.hasScroll = totalTableHeight > window.innerHeight;
    }
  }

  toggleAll(e: MatCheckboxChange, force = false) {
    if (force || e.checked) {
      return this.store.dispatch(new SelectMultiplePlayTracks(this.tracks));
    } else {
      return this.store.dispatch(new UnselectAll());
    }
  }

  getTableHeight(): number {
    if (this.processing) {
      return 350;
    }
    // Size * line height + header height
    if (this.tracksObservable$.value.length) {
      return this.tracksObservable$.value.length * 50 + 43;
    }

    return 236;
  }

  addTracks() {
    this.store.dispatch(
      new TryActionUnlessPanelIsBlocking(
        new OpenSpecificPanel(PANELS_KEYS.ADD_TO_PLAYLIST),
      ),
    );
  }

  uploadTracks() {
    this.modalService.playlistUploadBox(null, this.playlist);
  }

  @Dispatch()
  editPlaylist() {
    return new TryActionUnlessPanelIsBlocking(
      new OpenSpecificPanel(
        PANELS_KEYS.EDIT_PLAYLIST,
        new StartEditPlaylist(this.playlist),
      ),
    );
  }

  onSelectShiftLine(trackIdOrPosClicked: number) {
    if (this.lastSelectedTrackId === -1) {
      return;
    }
    this.tracksObservable$
      .asObservable()
      .pipe(take(1))
      .subscribe(tracks => {
        const startIdx = tracks.findIndex(
          track => this.getTrackId(track) === this.lastSelectedTrackId,
        );
        const endIdx = tracks.findIndex(
          track => this.getTrackId(track) === trackIdOrPosClicked,
        );
        if (endIdx === startIdx) {
          return;
        }
        let ids: PlaylistTrack[];
        if (endIdx < startIdx) {
          ids = tracks.slice(endIdx, startIdx + 1);
        } else {
          ids = tracks.slice(startIdx, endIdx + 1);
        }
        this.store.dispatch(
          new SelectMultiplePlayTracks(ids.filter(tra => !tra.processing)),
        );
        this.lastSelectedTrackId = trackIdOrPosClicked;
      });
  }

  addTracksToPlaylist() {
    const selectedTracks = this.store
      .selectSnapshot(SelectedTracksState.selectedPlayTracks)
      .map(val => val.id);
    this.modalService.addToPlaylist(selectedTracks);
  }

  editMultipleTracks(shortcut = false) {
    const selected = this.store
      .selectSnapshot(SelectedTracksState.selectedPlayTracks)
      .filter(t => !t.locked);
    if (!selected.length) {
      return;
    }
    let tracks: Track[] = [];
    selected.map(track =>
      this.store
        .select(TracksState.trackWithId)
        .pipe(map(fn => fn(track.id)))
        .subscribe(data => (tracks = [...tracks, data])),
    );
    if (tracks.length === 1) {
      this.editTrack(tracks[0], shortcut);
    } else {
      amplitude
        .getInstance()
        .logEvent(shortcut ? 'open_track_edition_shortcut' : 'open_track_edition');
      this.store.dispatch(new StartEditMultipleTracks(tracks));
    }
  }

  @Dispatch()
  removeTrackFromPlaylist = (track: PlaylistTrack) =>
    new DeletePlaylistTracksRequest(this.playlist.id, [track]);

  @Dispatch()
  removeSelectedTracksFromPlaylist = () => {
    const selectedTracks = this.store.selectSnapshot(
      SelectedTracksState.selectedPlayTracks,
    );

    return new DeletePlaylistTracksRequest(this.playlist.id, selectedTracks);
  };

  archiveTrack(track: Track, shortcut = false) {
    const targetBac = this.store.selectSnapshot(BacsState.bacById)(track.idTrackbox);
    this.modalService.archiveTrack(track, targetBac, shortcut);
  }

  archiveMultiple(shortcut = false) {
    this.modalService.archiveMultipleTracks(
      this.store
        .selectSnapshot(SelectedTracksState.selectedPlayTracks)
        .filter(t => !t.locked)
        .map(t => t.id)
        .filter((t, i, a) => a.indexOf(t) === i),
      null,
      shortcut,
    );
  }

  hasTags(track: PlaylistTrack = null): boolean {
    if (track) {
      return !!track.tags.length;
    }

    const trackWithId = this.store.selectSnapshot(TracksState.trackWithId);

    return (
      this.store
        .selectSnapshot(SelectedTracksState.selectedPlayTracks)
        // Redundant, but used to fetch the track in store, to be up to date with last tags changes
        .map(t => trackWithId(t.id))
        .filter(t => !!t)
        .some(t => t.tags.length > 0)
    );
  }

  removeTagsFromTracks(track: PlaylistTrack = null) {
    if (track) {
      this.modalService.removeTagsFromTracks([track]);
    } else {
      const trackWithId = this.store.selectSnapshot(TracksState.trackWithId);
      this.modalService.removeTagsFromTracks(
        this.store
          .selectSnapshot(SelectedTracksState.selectedPlayTracks)
          // Redundant, but used to fetch the track in store, to be up to date with last tags changes
          .map(t => trackWithId(t.id))
          .filter(t => !!t)
          .filter(t => !t.locked),
      );
    }
  }

  tracksLocked(track: PlaylistTrack = null): boolean {
    if (track) {
      return track.locked;
    }

    return this.store
      .selectSnapshot(SelectedTracksState.selectedPlayTracks)
      .every(t => t.locked);
  }

  addTagsToTrack(track: PlaylistTrack = null) {
    if (track) {
      this.modalService.addTagsToTrack([track.id]);
    } else {
      this.modalService.addTagsToTrack(
        this.store
          .selectSnapshot(SelectedTracksState.selectedPlayTracks)
          .filter(t => !t.locked)
          .map(t => t.id),
      );
    }
  }

  isMultiSelect(positionOrId: number) {
    const selectedTracks = this.store.selectSnapshot(
      SelectedTracksState.selectedPlayTracks,
    );

    return this.isManualPlaylist()
      ? selectedTracks.length > 1 &&
          selectedTracks.findIndex(t => t.position === positionOrId) > -1
      : selectedTracks.length > 1 &&
          selectedTracks.findIndex(t => t.id === positionOrId) > -1;
  }

  editMixpointTrack(track: Track) {
    this.store.dispatch(new TrackCuesRequest(track));
    this.modalService.editMixPoints(track);
  }

  isManualPlaylist() {
    return this.playlist.type === 'manual';
  }

  onDeleteHotkey() {
    if (this.trackOpen && !this.actionMultipleTrack()) {
      this.archiveTrack(this.trackOpen, true);
    } else if (this.store.selectSnapshot(SelectedTracksState.selectedPlayTracks).length) {
      this.archiveMultiple(true);
    }
  }

  selectFirstTrack() {
    this.tracksObservable$
      .asObservable()
      .pipe(take(1))
      .subscribe((tracks: PlaylistTrack[]) => {
        if (!tracks.length) {
          return;
        }
        const focusedTrack = tracks[0];
        this.store.dispatch(new SelectPlayTrack(focusedTrack));
      });
  }

  selectNextTrack(key: string, shiftPressed = false) {
    this.tracksObservable$
      .asObservable()
      .pipe(take(1))
      .subscribe((tracks: PlaylistTrack[]) => {
        const startIdx = tracks.findIndex(
          track => this.getTrackId(track) === this.lastSelectedTrackId,
        );
        let newIdx =
          this.lastArrow !== '' && key !== this.lastArrow && shiftPressed
            ? startIdx
            : startIdx + (key === 'ArrowDown' ? 1 : -1);
        if (newIdx < 0 || newIdx >= tracks.length) {
          this.checkTableScroll(newIdx, key);

          return;
        }
        let newFocusedTrack = tracks[newIdx];

        if (!shiftPressed) {
          this.store.dispatch(new UnselectAll());
          this.store.dispatch(new SelectPlayTrack(newFocusedTrack, true));
          this.lastArrow = '';
          this.selectedWithShift = [];
          this.checkTableScroll(newIdx, key);

          return;
        }

        if (
          this.getTrackId(newFocusedTrack) ===
          this.store.selectSnapshot(SelectedTracksState.lastClicked)
        ) {
          newIdx = newIdx + (key === 'ArrowDown' ? 1 : -1);
          newIdx = Math.min(tracks.length - 1, Math.max(0, newIdx));
          newFocusedTrack = tracks[newIdx];
        }
        this.checkTableScroll(newIdx, key);
        const newFocusedId = this.getTrackId(newFocusedTrack);

        if (this.selectedWithShift.indexOf(newFocusedId) > -1) {
          this.store.dispatch(new UnselectPlayTrack(newFocusedTrack));
          this.selectedWithShift = this.selectedWithShift.filter(
            id => id !== newFocusedId,
          );
        } else {
          this.store.dispatch(new SelectPlayTrack(newFocusedTrack));
          this.selectedWithShift.push(newFocusedId);
        }
        this.lastSelectedTrackId = newFocusedId;
        this.lastArrow = key;
      });
  }

  getTrackId(track: PlaylistTrack) {
    return track.position ? track.position : track.id;
  }
}
